import React, { useState } from 'react';
import { X, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

/*
 * UpgradeModal — lands here from the "Upgrade to Premium" ad click-through (/upgrade).
 * There's no payment processor wired up yet (adult content isn't eligible for
 * Stripe/PayPal), so logged-in users can only self-serve a "request" for Premium;
 * an admin follows up and grants it manually from the Ads panel's Users tab.
 */
export default function UpgradeModal({ onClose, currentUser, onNeedAuth, onRequested }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const plan = currentUser?.plan || 'free';

  const requestPremium = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/premium/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || 'Something went wrong'); return; }
      onRequested(data.user);
    } catch (e) {
      setErr('Network error, please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-sm p-6 text-white text-center space-y-4">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-800" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-center gap-2 text-amber-400">
          <Crown className="w-6 h-6" />
          <h2 className="text-xl font-bold text-white">Go Premium</h2>
        </div>
        <p className="text-sm text-zinc-400">No ads in the feed or the player. Ever.</p>

        {!currentUser ? (
          <button
            onClick={onNeedAuth}
            className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
          >
            Sign up to continue
          </button>
        ) : plan === 'premium' ? (
          <p className="text-green-400 text-sm font-medium">You already have Premium — enjoy the ad-free feed!</p>
        ) : plan === 'premium_requested' ? (
          <p className="text-amber-400 text-sm">Request received — we'll reach out to set up payment shortly.</p>
        ) : (
          <>
            <p className="text-xs text-zinc-500">
              We're still setting up secure payment for Premium. Tap below and we'll follow up with you directly.
            </p>
            <button
              onClick={requestPremium}
              disabled={busy}
              className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {busy ? 'Requesting…' : 'Request Premium access'}
            </button>
            {err && <p className="text-red-400 text-xs">{err}</p>}
          </>
        )}
      </div>
    </div>
  );
}
